/**
 * Last-resort recovery screen shown when the app fails to boot or crashes
 * at render time. Rendered by AppErrorBoundary and by the synchronous
 * try/catch in main.tsx.
 *
 * Intentionally has zero external dependencies — no Ionic, no theme store,
 * no Tailwind reliance — because the very subsystem that crashed may be
 * one of those. Uses inline styles + a plain <img> for the logo so it
 * renders even if CSS failed to load.
 *
 * Color #8f4420 and fonts Open Sans / Montserrat are hand-copied from
 * src/theme/variables.scss (--ion-color-primary, --font, --font-heading).
 * Keep in sync if the design tokens change. The inline boot watchdog in
 * index.html duplicates the same values for the same reason.
 */
export const SnagScreen: React.FC = () => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        color: '#1a1a1a',
        fontFamily:
          '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
      }}
    >
      <img
        src="/images/HomePlatedTM.svg"
        alt="Homeplated"
        style={{ width: '180px', maxWidth: '60%', height: 'auto', marginBottom: '32px' }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = '/images/HomePlatedTM.png';
        }}
      />

      <h1
        style={{
          fontFamily:
            '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          margin: '0 0 12px 0',
          color: '#8f4420',
        }}
      >
        We hit a snag
      </h1>

      <p
        style={{
          fontSize: '15px',
          lineHeight: 1.5,
          maxWidth: '360px',
          margin: '0 0 32px 0',
          color: '#555',
        }}
      >
        Something went wrong while loading Homeplated. Give it another try — we
        usually bounce back on a reload.
      </p>

      <button
        type="button"
        onClick={handleReload}
        style={{
          appearance: 'none',
          border: 'none',
          backgroundColor: '#8f4420',
          color: '#ffffff',
          fontFamily: 'inherit',
          fontSize: '16px',
          fontWeight: 600,
          padding: '14px 32px',
          borderRadius: '999px',
          cursor: 'pointer',
          minWidth: '200px',
        }}
      >
        Reload
      </button>
    </div>
  );
};
